import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CoverageAreaDetailsForm } from './CoverageAreaDetailsForm';
import { Edit } from 'lucide-react';
import { useToast } from 'src/components/useToast';
import { trpc } from 'src/trpc';

interface EditCoverageAreaModalProps {
  coverageAreaId: string;
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

export const EditCoverageAreaModal = ({
  coverageAreaId,
  onSuccess,
  trigger,
}: EditCoverageAreaModalProps) => {
  const [open, setOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  const handleSuccess = () => {
    successToast({
      title: 'Coverage Area Updated',
      description: 'Coverage area has been successfully updated',
    });
    setOpen(false);
    utils.admin.organization.getOrganizationCoverageAreas.invalidate();
    utils.admin.organization.getUnassignedCoverageAreas.invalidate();
    onSuccess();
  };

  const handleError = (error: Error) => {
    errorToast({
      title: 'Failed to update coverage area',
      description: error.message || 'An error occurred while updating the coverage area',
    });
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm">
      <Edit className="h-4 w-4" />
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Coverage Area</DialogTitle>
          <DialogDescription>Update the details for this coverage area.</DialogDescription>
        </DialogHeader>
        <CoverageAreaDetailsForm
          coverageAreaId={coverageAreaId}
          onSuccess={handleSuccess}
          onError={handleError}
          onStateChange={(state) => setIsFormValid(state.isValid)}
        >
          <div className="flex flex-row justify-end gap-4 mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button disabled={!isFormValid} type="submit">
              Update Coverage Area
            </Button>
          </div>
        </CoverageAreaDetailsForm>
      </DialogContent>
    </Dialog>
  );
};
